import { computed, inject, Service } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';

import { RequiredDocumentType } from '@documents/models/document-type';
import { PermissionsService } from '@shared/services/permissions.service';
import environment from '@environments/environment';

/** Shared empty list, so a not-loaded read keeps a stable identity. */
const NO_DOCUMENT_TYPES: RequiredDocumentType[] = [];

/**
 * Page size the reviewer-document-type endpoint has always been called with.
 * Kept as-is so this stays a refactor; there are five types today, but the
 * Edit dialog's list would truncate past ten.
 */
const REVIEWER_DOCUMENT_TYPE_PAGE_SIZE = 10;

/**
 * ERM-12599 — the reviewer-material document types.
 *
 * Immutable reference data: the endpoint is the only writer and nothing else
 * reads it, so unlike the materials list there is no store slice worth
 * keeping. That makes it the one read here that is genuinely just a URL, and
 * `httpResource` is the direct expression of it — no ngrx round trip, and
 * `isLoading` / `error` / `reload()` come for free.
 *
 * Auto-provided app-wide on purpose. The types do not change per submission,
 * so one fetch per session is right — the same thing the old service's
 * `loadedDocumentTypes` flag was hand-rolling.
 */
@Service()
export class ReviewerDocumentTypesSignalStore {
  private readonly _permissionsService = inject(PermissionsService);

  private readonly _$isInternal = toSignal(
    this._permissionsService.isInternal$,
    { initialValue: false },
  );

  /**
   * Undefined URL while the viewer is not internal, which leaves the resource
   * idle rather than firing a request an external user cannot use.
   */
  private readonly _documentTypes = httpResource<{
    data: RequiredDocumentType[];
  }>(() =>
    this._$isInternal()
      ? `${environment.irisApi}/reviewer-document-type/?page=1&page_size=${REVIEWER_DOCUMENT_TYPE_PAGE_SIZE}`
      : undefined,
  );

  /**
   * Guarded on `hasValue` because a resource's `value` throws while it is in
   * its error state.
   */
  readonly $documentTypes = computed<RequiredDocumentType[]>(() =>
    this._documentTypes.hasValue()
      ? (this._documentTypes.value()?.data ?? NO_DOCUMENT_TYPES)
      : NO_DOCUMENT_TYPES,
  );

  readonly $isLoading = computed<boolean>(() =>
    this._documentTypes.isLoading(),
  );

  /** Re-reads the list, for when a type is added from admin. */
  reload(): void {
    this._documentTypes.reload();
  }
}
